import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { TicketSpecDTO } from '@harness/shared';
import { JiraIcon, Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import { relTime } from '../../lib/format';
import { useConversation, type ProjectDetail } from '../../lib/queries';
import { useCan, VIEWER_HINT } from '../../lib/role';
import { toast } from '../../lib/store';

// Tickets mode side panel: the Researcher's spec, then Approve → Create in Jira. Creating uses exactly
// the approved version; if the Researcher revises it, it needs approving again.

export const specKey = (convId: string) => ['ticket-spec', convId] as const;

function List({ title, items, tone }: { title: string; items?: string[]; tone?: 'ask' | 'idea' }) {
  if (!items?.length) return null;
  return (
    <div className={`spec-sec ${tone ?? ''}`}>
      <div className="micro">{title}</div>
      <ul>
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Text({ title, body }: { title: string; body?: string }) {
  if (!body?.trim()) return null;
  return (
    <div className="spec-sec">
      <div className="micro">{title}</div>
      <p>{body}</p>
    </div>
  );
}

export default function TicketPanel({ project, convId }: { project: ProjectDetail; convId?: string }) {
  const qc = useQueryClient();
  const can = useCan();
  const conv = useConversation(convId);
  const spec = useQuery({ queryKey: specKey(convId ?? '-'), queryFn: () => api.get<TicketSpecDTO | null>(`/api/conversations/${convId}/ticket-spec`), enabled: !!convId });
  const types = useQuery({ queryKey: ['jira-issue-types', project.id], queryFn: () => api.get<string[]>(`/api/projects/${project.id}/jira/issue-types`), staleTime: 10 * 60_000 });
  const [issueType, setIssueType] = useState('');
  const [busy, setBusy] = useState(false);
  const s = spec.data;
  const working = conv.data?.conversation.status === 'running';

  useEffect(() => {
    if (!types.data?.length || issueType) return;
    const want = s?.spec.issueType;
    setIssueType(types.data.find((t) => t.toLowerCase() === want?.toLowerCase()) ?? types.data.find((t) => t === 'Story') ?? types.data[0]!);
  }, [types.data, s?.spec.issueType, issueType]);

  if (!convId) return <div className="empty">Start a ticket conversation: the spec appears here as the Researcher drafts it.</div>;
  if (spec.isLoading) return <Spinner />;
  if (!s)
    return (
      <div className="empty">
        {working ? 'The Researcher is reading the code and drafting the spec…' : 'No spec yet. Describe what you need in the chat; the Researcher drafts the spec here and asks questions where it needs to.'}
      </div>
    );

  const act = async (path: string, body: unknown, done: string) => {
    setBusy(true);
    try {
      qc.setQueryData(specKey(convId), await api.post<TicketSpecDTO>(`/api/conversations/${convId}/ticket-spec/${path}`, body));
      toast(done);
    } catch (e) {
      toast(errorText(e), 'error');
      void spec.refetch();
    } finally {
      setBusy(false);
    }
  };
  const t = s.spec;

  return (
    <div className="stack-sm ticket-spec">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <span className={`tag sm ${s.status === 'created' ? 'tag-done' : s.status === 'approved' ? 'tag-gate' : ''}`}>
          {s.status === 'created' ? `Created · ${s.jiraKey}` : s.status === 'approved' ? 'Approved' : 'Draft'}
        </span>
        <span className="faint" style={{ fontSize: 12 }}>
          v{s.version} · {relTime(s.updatedAt)}
        </span>
      </div>
      <div className="title-900" style={{ fontSize: 18, lineHeight: 1.25 }}>
        {t.title}
      </div>
      <p style={{ margin: 0, fontSize: 14 }}>{t.summary}</p>
      <Text title="Background" body={t.background} />
      <List title="Requirements" items={t.requirements} />
      <List title="Acceptance criteria" items={t.acceptanceCriteria} />
      <List title="Out of scope" items={t.outOfScope} />
      <Text title="Technical notes" body={t.technicalNotes} />
      <List title="Risks" items={t.risks} />
      <List title="Suggestions" items={t.suggestions} tone="idea" />
      <List title="Open questions" items={t.openQuestions} tone="ask" />
      {!!t.labels?.length && (
        <div className="row" style={{ gap: 6 }}>
          {t.labels.map((l) => (
            <span key={l} className="tag sm">
              {l}
            </span>
          ))}
        </div>
      )}

      <div className="spec-actions">
        {s.status === 'draft' && (
          <>
            {!!t.openQuestions?.length && <div className="hint">There are open questions. Answer them in the chat first, or approve if they don't block the ticket.</div>}
            <button
              className="btn btn-primary btn-block"
              disabled={busy || working || !can.edit}
              title={!can.edit ? VIEWER_HINT : working ? 'Wait for the Researcher to finish' : undefined}
              onClick={() => void act('approve', { version: s.version }, `Spec v${s.version} approved`)}
            >
              <Check className="icon" />
              Approve spec
            </button>
          </>
        )}
        {s.status === 'approved' && (
          <>
            <div className="hint">
              Approved by {s.approvedBy} {s.approvedAt ? relTime(s.approvedAt) : ''}. Any change in the chat needs approving again.
            </div>
            <div className="row" style={{ flexWrap: 'nowrap', gap: 8 }}>
              <select className="input" value={issueType} onChange={(e) => setIssueType(e.target.value)} aria-label="Issue type" style={{ flex: 1 }}>
                {(types.data ?? [issueType || 'Story']).map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                disabled={busy || working || !can.edit || !issueType}
                onClick={() => void act('create-issue', { version: s.version, issueType }, 'Ticket created on the Jira board')}
              >
                <JiraIcon className="icon" />
                Create in Jira
              </button>
            </div>
          </>
        )}
        {s.status === 'created' && s.jiraUrl && (
          <a className="btn btn-secondary btn-block" href={s.jiraUrl} target="_blank" rel="noopener noreferrer">
            <JiraIcon className="icon" />
            Open {s.jiraKey} in Jira
            <ExternalLink className="icon" />
          </a>
        )}
      </div>
    </div>
  );
}
